import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import styles from "./BulkSelectCheckbox.module.css";

export interface BulkSelectCheckboxProps {
  isSelected: boolean;
  onChange: (nextSelected: boolean) => void;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Rounded multi-select checkbox overlaid on message list cards.
 *
 * Implementation notes:
 * - The checkbox lives inside a `@adobe/react-spectrum` ListBox `Item`, whose
 *   rendered subtree is cached by `@react-stately/collections` and
 *   `@react-aria/virtualizer` keyed by the item reference. That means a new
 *   `isSelected` prop passed from the parent after a bulk-selection state
 *   change is not guaranteed to reach this component on every update.
 *   To stay responsive we keep a small local `visual` state that is flipped
 *   optimistically on every click and re-synced from `isSelected` whenever
 *   fresh props do arrive. The parent's bulk-selection state still drives
 *   downstream bulk actions (delete / move selected).
 * - We attach a document-level capture-phase `click` listener scoped to this
 *   element so the click wins the race against ListBox's own `usePress`
 *   handlers. `stopPropagation` is deliberate - otherwise the same click
 *   would also select the row and open the email detail.
 */
const BulkSelectCheckbox: React.FC<BulkSelectCheckboxProps> = ({
  isSelected,
  onChange,
  ariaLabel = "Select message",
  className,
  style,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [visual, setVisual] = useState<boolean>(isSelected);
  const onChangeRef = useRef(onChange);
  const visualRef = useRef<boolean>(isSelected);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    setVisual(isSelected);
    visualRef.current = isSelected;
  }, [isSelected]);

  // Document-level capture listener ensures we toggle even when react-aria's
  // press handlers on the parent ListBox Item would otherwise consume the
  // click first.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const handlePointerOrClick = (event: Event) => {
      if (!el.contains(event.target as Node)) return;
      event.stopPropagation();
      if (event.type === "click") {
        event.preventDefault();
        const next = !visualRef.current;
        visualRef.current = next;
        setVisual(next);
        try {
          onChangeRef.current(next);
        } catch {
          // Swallow so a downstream error doesn't block the visual toggle.
        }
      }
    };

    el.addEventListener("pointerdown", handlePointerOrClick, { capture: true });
    el.addEventListener("mousedown", handlePointerOrClick, { capture: true });
    el.addEventListener("touchstart", handlePointerOrClick, { capture: true });
    el.addEventListener("click", handlePointerOrClick, { capture: true });

    return () => {
      el.removeEventListener("pointerdown", handlePointerOrClick, {
        capture: true,
      });
      el.removeEventListener("mousedown", handlePointerOrClick, {
        capture: true,
      });
      el.removeEventListener("touchstart", handlePointerOrClick, {
        capture: true,
      });
      el.removeEventListener("click", handlePointerOrClick, { capture: true });
    };
  }, []);

  return (
    <div
      ref={rootRef}
      role="checkbox"
      tabIndex={0}
      aria-checked={visual}
      aria-label={ariaLabel}
      className={clsx(
        styles.checkbox,
        visual && styles.checkboxSelected,
        className,
      )}
      style={style}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          setVisual((current) => {
            const next = !current;
            visualRef.current = next;
            try {
              onChangeRef.current(next);
            } catch {
              // Swallow so a downstream error doesn't block the visual toggle.
            }
            return next;
          });
        }
      }}
    >
      {visual && (
        <svg
          className={styles.checkIcon}
          viewBox="0 0 16 16"
          width={12}
          height={12}
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M3.5 8.5L6.5 11.5L12.5 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

export default BulkSelectCheckbox;
